import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "-";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function getToken() {
  return localStorage.getItem("palantir_token");
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

function levelClass(level) {
  const normalized = String(level || "").toUpperCase();
  if (normalized === "ERROR") return "text-danger";
  if (normalized === "WARN") return "text-warning";
  return "text-info";
}

function compactPayload(entry) {
  const payload = entry?.payload || {};
  const copy = { ...payload };
  delete copy.scope;
  delete copy.event;
  delete copy.method;
  delete copy.path;
  delete copy.status;
  delete copy.duration_ms;
  delete copy.user_id;
  delete copy.active_business_unit_id;
  return copy;
}

export default function AdminSecurityLogs() {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [entries, setEntries] = useState([]);
  const [dir, setDir] = useState("");
  const [fileMeta, setFileMeta] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [error, setError] = useState("");
  const [keepDays, setKeepDays] = useState(180);
  const [limit, setLimit] = useState(200);

  const stats = useMemo(() => {
    const out = {
      total: entries.length,
      warnError: 0,
      loginFailed: 0,
      identityChanges: 0,
      uniqueUsers: new Set(),
    };

    entries.forEach((entry) => {
      const level = String(entry.level || "").toUpperCase();
      const event = String(entry.event || "").toUpperCase();
      if (level === "WARN" || level === "ERROR") out.warnError += 1;
      if (event === "LOGIN_FAILED") out.loginFailed += 1;
      if (
        event === "USER_CREATED" ||
        event === "USER_UPDATED" ||
        event === "USER_DELETED" ||
        event === "ACTIVE_BUSINESS_UNIT_CHANGED" ||
        event === "PASSWORD_RESET_COMPLETED"
      ) {
        out.identityChanges += 1;
      }
      if (entry.user_id != null) out.uniqueUsers.add(String(entry.user_id));
    });

    return {
      ...out,
      uniqueUsers: out.uniqueUsers.size,
    };
  }, [entries]);

  async function loadFiles({ keepSelection = true } = {}) {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/security-logs`, {
        headers: authHeaders(),
      });
      const nextFiles = Array.isArray(res.data?.files) ? res.data.files : [];
      setFiles(nextFiles);
      setDir(String(res.data?.dir || ""));

      const latest = nextFiles.find((file) => file.name === "security-latest.txt");
      const fallback = latest?.name || nextFiles[0]?.name || "";
      const stillExists = nextFiles.some((file) => file.name === selectedFile);
      const nextSelected = keepSelection && stillExists ? selectedFile : fallback;
      setSelectedFile(nextSelected);

      if (nextSelected) {
        await loadEntries(nextSelected);
      } else {
        setEntries([]);
        setFileMeta(null);
      }
    } catch (e) {
      console.error("[AdminSecurityLogs][loadFiles] error:", e);
      setError("Failed to load security logs.");
    } finally {
      setLoading(false);
    }
  }

  async function loadEntries(name = selectedFile) {
    if (!name) return;
    setLoadingEntries(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/security-logs/${encodeURIComponent(name)}/entries`,
        {
          params: { limit },
          headers: authHeaders(),
        }
      );
      setEntries(Array.isArray(res.data?.entries) ? res.data.entries : []);
      setTruncated(!!res.data?.truncated);
      setFileMeta({
        size: res.data?.size ?? null,
        mtime: res.data?.mtime ?? null,
      });
    } catch (e) {
      console.error("[AdminSecurityLogs][loadEntries] error:", e);
      setError("Failed to load security log entries.");
    } finally {
      setLoadingEntries(false);
    }
  }

  useEffect(() => {
    loadFiles({ keepSelection: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFileChange(e) {
    const name = e.target.value;
    setSelectedFile(name);
    await loadEntries(name);
  }

  async function handleDownload(name) {
    if (!name) return;
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/security-logs/${encodeURIComponent(name)}/download`,
        {
          headers: authHeaders(),
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[AdminSecurityLogs][download] error:", e);
      setError("Failed to download security log.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(name) {
    if (!name || name === "security-latest.txt") return;
    const ok = window.confirm(`Delete security log file "${name}"?`);
    if (!ok) return;

    try {
      setLoading(true);
      setError("");
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/security-logs/${encodeURIComponent(name)}`,
        { headers: authHeaders() }
      );
      await loadFiles({ keepSelection: false });
    } catch (e) {
      console.error("[AdminSecurityLogs][delete] error:", e);
      setError("Failed to delete security log.");
    } finally {
      setLoading(false);
    }
  }

  async function handleCleanup() {
    const n = Number(keepDays);
    if (!Number.isFinite(n) || n < 1) {
      setError("Keep days must be >= 1.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/security-logs/cleanup`,
        { keep_days: n },
        { headers: authHeaders() }
      );
      await loadFiles({ keepSelection: true });
    } catch (e) {
      console.error("[AdminSecurityLogs][cleanup] error:", e);
      setError("Failed to cleanup security logs.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="adminCard">
      <div className="adminCardHeader">
        <h2 className="adminCardTitle">Security Logs</h2>
        <div className="adminCardSubtitle">
          {dir ? `Directory: ${dir}` : "Dedicated security telemetry"}
        </div>
      </div>

      <div className="adminCardInner">
        {error ? (
          <div className="alert alert-danger" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <button
            className="btn btn-sm btn-outline-light"
            type="button"
            onClick={() => loadFiles({ keepSelection: true })}
            disabled={loading || loadingEntries}
          >
            Refresh
          </button>

          <select
            className="form-select form-select-sm"
            style={{ width: 260 }}
            value={selectedFile}
            onChange={handleFileChange}
            disabled={loading || loadingEntries || !files.length}
          >
            {files.length ? (
              files.map((file) => (
                <option key={file.name} value={file.name}>
                  {file.name}
                </option>
              ))
            ) : (
              <option value="">No files</option>
            )}
          </select>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ opacity: 0.8 }}>Rows:</span>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 90 }}
              value={limit}
              min={1}
              max={1000}
              onChange={(e) => setLimit(e.target.value)}
              disabled={loading || loadingEntries}
            />
            <button
              className="btn btn-sm btn-outline-info"
              type="button"
              onClick={() => loadEntries()}
              disabled={loading || loadingEntries || !selectedFile}
            >
              Load
            </button>
          </div>

          <button
            className="btn btn-sm btn-outline-info"
            type="button"
            onClick={() => handleDownload(selectedFile)}
            disabled={loading || loadingEntries || !selectedFile}
          >
            Download
          </button>

          <button
            className="btn btn-sm btn-outline-danger"
            type="button"
            onClick={() => handleDelete(selectedFile)}
            disabled={loading || loadingEntries || !selectedFile || selectedFile === "security-latest.txt"}
          >
            Delete
          </button>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ opacity: 0.8 }}>Keep days:</span>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 110 }}
              value={keepDays}
              min={1}
              onChange={(e) => setKeepDays(e.target.value)}
              disabled={loading || loadingEntries}
            />
            <button
              className="btn btn-sm btn-outline-warning"
              type="button"
              onClick={handleCleanup}
              disabled={loading || loadingEntries}
            >
              Cleanup
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          {[
            ["Entries", stats.total],
            ["Warn/Error", stats.warnError],
            ["Login Failed", stats.loginFailed],
            ["Identity Changes", stats.identityChanges],
            ["Users", stats.uniqueUsers],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                minWidth: 130,
                padding: "10px 12px",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 8,
                background: "rgba(255,255,255,0.035)",
              }}
            >
              <div style={{ fontSize: "0.72rem", opacity: 0.68 }}>{label}</div>
              <div style={{ fontWeight: 850, color: "rgba(230,230,247,0.94)" }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ opacity: 0.72, fontSize: "0.82rem", marginBottom: 10 }}>
          {selectedFile ? (
            <>
              {selectedFile}
              {fileMeta ? ` · ${formatBytes(fileMeta.size)} · ${formatDate(fileMeta.mtime)}` : ""}
              {truncated ? " · tail view" : ""}
            </>
          ) : (
            "No security log selected."
          )}
        </div>

        <div
          className="table-responsive"
          style={{
            maxHeight: "560px",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <table
            className="table table-dark table-striped table-sm align-middle mb-0"
            style={{
              fontSize: "0.76rem",
              lineHeight: "1.0rem",
              tableLayout: "auto",
              width: "auto",
              minWidth: "1180px",
              whiteSpace: "nowrap",
            }}
          >
            <thead>
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Scope</th>
                <th>Event</th>
                <th>User</th>
                <th>BU</th>
                <th>Method</th>
                <th>Path</th>
                <th>Status</th>
                <th>ms</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.length ? (
                entries.map((entry) => {
                  const details = compactPayload(entry);
                  return (
                    <tr key={`${entry.id}-${entry.timestamp || ""}-${entry.event || ""}`}>
                      <td>{entry.timestamp || "-"}</td>
                      <td className={levelClass(entry.level)}>{entry.level || "-"}</td>
                      <td>{entry.scope || "-"}</td>
                      <td>{entry.event || "-"}</td>
                      <td>{entry.user_id ?? "-"}</td>
                      <td>{entry.active_business_unit_id ?? "-"}</td>
                      <td>{entry.method || "-"}</td>
                      <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {entry.path || "-"}
                      </td>
                      <td>{entry.status ?? "-"}</td>
                      <td>{entry.duration_ms ?? "-"}</td>
                      <td style={{ minWidth: 220, maxWidth: 420 }}>
                        <details>
                          <summary style={{ cursor: "pointer" }}>Payload</summary>
                          <pre
                            style={{
                              margin: "8px 0 0",
                              maxWidth: 520,
                              maxHeight: 180,
                              overflow: "auto",
                              whiteSpace: "pre-wrap",
                              color: "rgba(230,230,247,0.78)",
                            }}
                          >
                            {JSON.stringify(details, null, 2)}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} style={{ opacity: 0.75 }}>
                    {loading || loadingEntries ? "Loading..." : "No security entries found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
